import type { Phase1Data } from '../api/extractionApi';
import type { LocationInput } from '@/modules/settings/api/locationsApi';
import { formatOfficeHours, DAYS } from '../utils/officeHoursHelpers';

export interface ProfileField {
  key: string;
  label: string;
  confidence: 'high' | 'medium' | 'low';
  /** The editable value. Empty string when nothing is known — never a placeholder. */
  getValue: (ext: Phase1Data) => string;
  /** Shown instead of an empty value. Display only; never saved. */
  emptyLabel?: string;
  hasValue?: (ext: Phase1Data) => boolean;
  setValue?: (draft: Phase1Data, val: string) => void;
  readonly?: boolean;
}

export const parseCSV = (val: string): string[] =>
  val
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * Post-nominal segments that continue the preceding name instead of starting a
 * new one. Mirrors NAME_SUFFIXES in backend/src/modules/staff/staffNames.ts —
 * the backend re-joins defensively, but parsing correctly here stops the bad
 * shape being produced at all.
 */
const NAME_SUFFIXES = new Set([
  'dds',
  'dmd',
  'rdh',
  'rda',
  'md',
  'do',
  'mbbs',
  'pa',
  'pac',
  'np',
  'aprn',
  'crna',
  'rn',
  'lpn',
  'od',
  'dpm',
  'dc',
  'phd',
  'ms',
  'msc',
  'ma',
  'mph',
  'mba',
  'bsc',
  'bs',
  'ba',
  'esq',
  'facc',
  'facs',
  'facp',
  'faap',
  'facog',
  'faaos',
  'fags',
  'fagd',
  'ficoi',
  'faaid',
  'jr',
  'sr',
  'ii',
  'iii',
  'iv',
  'v',
]);

function isSuffixSegment(segment: string): boolean {
  const trimmed = segment.trim();
  if (!trimmed) return false;
  const parts = trimmed.split(/[\s/]+/).filter(Boolean);
  if (!parts.length || parts.length > 3) return false;
  return parts.every((p) => NAME_SUFFIXES.has(p.toLowerCase().replace(/[.\s]/g, '')));
}

/**
 * Parse a comma-separated provider list without severing credentials.
 *
 * Providers are stored as "Dr. Beni R. Verma, MD", so a plain CSV split turned
 * one person into two and produced a staff member literally called "MD".
 */
export const parseProviderNames = (val: string): string[] => {
  const out: string[] = [];
  for (const raw of val.split(',')) {
    const segment = raw.trim();
    if (!segment) continue;
    if (out.length > 0 && isSuffixSegment(segment)) {
      out[out.length - 1] = `${out[out.length - 1]}, ${segment}`;
      continue;
    }
    out.push(segment);
  }
  return out;
};

export function parseAddressString(full: string): Partial<LocationInput> {
  const parts = full
    .split(', ')
    .map((s) => s.trim())
    .filter(Boolean);
  if (parts.length >= 4) {
    return {
      address: parts.slice(0, -3).join(', '),
      city: parts[parts.length - 3],
      state: parts[parts.length - 2],
      zip: parts[parts.length - 1],
    };
  }
  if (parts.length === 3) {
    return { address: parts[0], city: parts[1], state: parts[2] };
  }
  return { address: full.trim() };
}

export const FIELDS: ProfileField[] = [
  {
    key: 'address',
    label: 'Address',
    confidence: 'medium',
    getValue: (ext) => ext.structuredAddress?.full || ext.addresses?.[0] || '',
    emptyLabel: 'Not detected',
    hasValue: (ext) => !!(ext.structuredAddress?.full || ext.addresses?.[0]),
    setValue: (draft, val) => {
      const trimmed = val.trim();
      draft.addresses = trimmed ? [trimmed] : [];
      if (!trimmed) {
        draft.structuredAddress = undefined;
        return;
      }
      // Re-parse the edit so street/city/state/zip stay in step with what was
      // typed — updating only `full` left stale city/state/zip behind.
      const parsed = parseAddressString(trimmed);
      draft.structuredAddress = {
        street: parsed.address,
        city: parsed.city,
        state: parsed.state,
        zip: parsed.zip,
        full: trimmed,
      };
    },
  },
  {
    key: 'services',
    label: 'Services Offered',
    confidence: 'medium',
    getValue: (ext) => ext.services?.join(', ') || '',
    emptyLabel: 'Not detected',
    hasValue: (ext) => !!ext.services?.length,
    setValue: (draft, val) => {
      draft.services = parseCSV(val);
    },
  },
  {
    key: 'providers',
    label: 'Providers',
    confidence: 'medium',
    getValue: (ext) => ext.providers?.join(', ') || '',
    emptyLabel: 'Not detected',
    hasValue: (ext) => !!ext.providers?.length,
    setValue: (draft, val) => {
      draft.providers = parseProviderNames(val);
    },
  },
  {
    key: 'technology',
    label: 'Technology & Equipment',
    confidence: 'medium',
    getValue: (ext) => ext.technology?.join(', ') || '',
    emptyLabel: 'Not detected',
    hasValue: (ext) => !!ext.technology?.length,
    setValue: (draft, val) => {
      draft.technology = parseCSV(val);
    },
  },
  {
    key: 'insurances',
    label: 'Insurance Accepted',
    confidence: 'medium',
    getValue: (ext) => ext.insurances?.join(', ') || '',
    emptyLabel: 'Not detected',
    hasValue: (ext) => !!ext.insurances?.length,
    setValue: (draft, val) => {
      draft.insurances = parseCSV(val);
    },
  },
  {
    key: 'languages',
    label: 'Languages Spoken',
    confidence: 'medium',
    getValue: (ext) => ext.languages?.join(', ') || 'English',
    hasValue: (ext) => !!ext.languages?.length,
    setValue: (draft, val) => {
      draft.languages = parseCSV(val);
    },
  },
  {
    key: 'membershipPlans',
    label: 'Membership Plans',
    confidence: 'medium',
    getValue: (ext) =>
      ext.membershipPlans?.length
        ? ext.membershipPlans.map((p) => `${p.name}${p.cost ? ` (${p.cost})` : ''}`).join(', ')
        : '',
    emptyLabel: 'No membership plans detected',
    hasValue: (ext) => ext.membershipPlans != null,
  },
  {
    key: 'paymentOptions',
    label: 'Payment & Financing Options',
    confidence: 'medium',
    getValue: (ext) => (ext.paymentOptions?.length ? ext.paymentOptions.join(', ') : ''),
    emptyLabel: 'No payment/financing options detected',
    hasValue: (ext) => ext.paymentOptions != null,
    setValue: (draft, val) => {
      draft.paymentOptions = parseCSV(val);
    },
  },
  {
    key: 'officeHours',
    label: 'Office Hours',
    confidence: 'medium',
    getValue: (ext) => formatOfficeHours(ext.officeHours),
    hasValue: (ext) => !!ext.officeHours && DAYS.some((d) => ext.officeHours?.[d.key]),
  },
];
